import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import { AsyncTypeahead } from 'react-bootstrap-typeahead';
import axios from 'axios';
import constants from '../../constants';
import { cookieStorageUtility } from '../../utils/utilityFunctions';
import airbnbApi from './api';
import { CodeSelect, ErrorAlert, PageLoader, PROPERTY_CURRENCIES } from './common';

const { serverUrl, ENCRYPTOR_KEY } = constants;
// Unit types are added from a multi-unit's room types, never created directly
const TOP_LEVEL_CLASSIFICATIONS = ['single-unit', 'multi-unit'];
const CLASSIFICATION_LABELS = {
  'single-unit': 'Single unit (one listing, e.g. a villa)',
  'multi-unit': 'Multi unit (hotel / villa complex with room types)',
  'unit-type': 'Unit type (room type)'
};
const AIRBNB_CHANNEL = 'AIR298';

const emptyDescription = () => ({ typeCode: '', language: 'en', text: '' });

const emptyForm = {
  gh_detail_id: '', name: '', type_code: '', classification: 'single-unit', onboarding_mode: 'takeover', max_occupancy: '',
  base_currency: '', license_code: '', airbnb_listing_id: '', airbnb_host_id: '', airbnb_channel_policy_id: '',
  descriptions: [emptyDescription()]
};

const Field = ({ label, required, children, md = 4, hint }) => (
  <Col md={md} className="margbot10">
    <Form.Label className="fnt13 mb-1">{label}{required && <span className="text-danger"> *</span>}</Form.Label>
    {children}
    {hint && <Form.Text className="fnt11">{hint}</Form.Text>}
  </Col>
);

// gethotellisting returns hotel IDs base64-encoded (e.g. "MjU4\n" for 258);
// gh_detail_id must be the plain gh_details.hotel_id.
const decodeHotelId = value => {
  const raw = String(value ?? '').trim();
  if (/^\d+$/.test(raw)) return raw;
  try {
    const decoded = atob(raw).trim();
    return /^\d+$/.test(decoded) ? decoded : '';
  } catch {
    return '';
  }
};

// Same hotel search the rest of RedCM uses (property/gethotellisting)
const HotelSearch = ({ onSelect }) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const search = hotelName => {
    setLoading(true);
    const authUserId = cookieStorageUtility.fetch('auth_user_id');
    const encryptedToken = cookieStorageUtility.fetch('encrypted_' + ENCRYPTOR_KEY + '_token');
    axios(serverUrl + 'property/gethotellisting', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        token: btoa(authUserId + '|' + encryptedToken)
      },
      data: { param: { page: 1, limit: 10, hotelName } }
    })
      .then(response => setOptions(response.data.status === 'success' ? response.data.data.list : []))
      .catch(() => setOptions([]))
      .finally(() => setLoading(false));
  };

  return (
    <AsyncTypeahead
      id="airbnb-hotel"
      isLoading={loading}
      options={options}
      labelKey="hotel"
      minLength={3}
      filterBy={['hotel', 'hotel_code']}
      onSearch={search}
      placeholder="Search hotel by name (min 3 letters)"
      onChange={selected => onSelect(selected[0] || null)}
      renderMenuItemChildren={option => (
        <span>{option.hotel} <span className="text-muted fnt12">({option.hotel_code}{option.city ? `, ${option.city}` : ''})</span></span>
      )}
    />
  );
};

const PropertyForm = () => {
  const { id, pmId } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(id ? null : emptyForm);
  const [property, setProperty] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [hotelName, setHotelName] = useState('');

  useEffect(() => {
    if (!id) return;
    airbnbApi.getProperty(id)
      .then(p => {
        setProperty(p);
        const merged = { ...emptyForm };
        Object.keys(emptyForm).forEach(key => {
          if (p[key] !== null && p[key] !== undefined) merged[key] = p[key];
        });
        if (!merged.descriptions.length) merged.descriptions = [emptyDescription()];
        // `name` in the API is the effective name; only the custom one is editable
        merged.name = p.custom_name || '';
        setForm(merged);
      })
      .catch(setError);
  }, [id]);

  if (!form) return <div className="pad20"><ErrorAlert error={error} />{!error && <PageLoader />}</div>;

  const set = (key, value) => setForm(prev => ({ ...prev, [key]: value }));
  const setDescription = (index, changes) => set('descriptions',
    form.descriptions.map((d, i) => (i === index ? { ...d, ...changes } : d)));

  const isUnitType = form.classification === 'unit-type';
  // Takeover maps an existing listing (single-unit / unit type); a multi-unit
  // takeover and every new listing go without one.
  const listingIdRequired = form.onboarding_mode === 'takeover' && form.classification !== 'multi-unit';
  const title = property ? (property.name || property.hotel_name) : '';

  const submit = e => {
    e.preventDefault();
    if (!id && !form.gh_detail_id) {
      setError({ message: 'Select the RedChannel hotel this Airbnb listing belongs to.' });
      return;
    }
    const payload = {
      ...form,
      max_occupancy: form.max_occupancy ? parseInt(form.max_occupancy, 10) : null,
      base_currency: form.base_currency || null,
      license_code: form.license_code || null,
      name: form.name.trim() || null,
      airbnb_listing_id: form.airbnb_listing_id || null,
      airbnb_host_id: form.airbnb_host_id || null,
      airbnb_channel_policy_id: form.airbnb_channel_policy_id || null,
      descriptions: form.descriptions.filter(d => d.text)
    };
    if (!listingIdRequired) payload.airbnb_listing_id = null;
    if (id) {
      delete payload.gh_detail_id;
      delete payload.classification;
    }
    setSaving(true);
    setError(null);
    const save = id ? airbnbApi.updateProperty(id, payload) : airbnbApi.createProperty(pmId, payload);
    save
      .then(p => navigate(`/airbnb/properties/${p.id}`))
      .catch(err => { setError(err); window.scrollTo(0, 0); })
      .finally(() => setSaving(false));
  };


  return (
    <div className="container-fluid pad20 text-start">
      <div className="margbot10 fnt13">
        <Link to="/airbnb">Property managers</Link>
        {pmId && <> / <Link to={`/airbnb/property-managers/${pmId}`}>Property manager</Link></>}
        {property && property.parent_id && (
          <> / <Link to={`/airbnb/properties/${property.parent_id}`}>{property.parent_name || property.hotel_name}</Link></>
        )}
        {property && <> / <Link to={`/airbnb/properties/${id}`}>{title}</Link></>}
      </div>
      <h4>{id ? `Edit ${isUnitType ? 'room type' : 'property'} — ${title}` : 'Add property'}</h4>
      <ErrorAlert error={error} onClose={() => setError(null)} />

      <Form onSubmit={submit}>
        <Card className="margbot15">
          <Card.Header className="bld">Property</Card.Header>
          <Card.Body>
            <Row>
              {!id && (
                <Field label="RedChannel hotel" required md={8}
                  hint="Name, address, location and country come from the hotel's RedChannel details.">
                  <HotelSearch onSelect={hotel => {
                    set('gh_detail_id', hotel ? decodeHotelId(hotel.hotel_id ?? hotel.hotelId) : '');
                    setHotelName(hotel ? hotel.hotel : '');
                  }} />
                </Field>
              )}
              <Field label="Name on Airbnb" md={8}
                hint={'Optional. Blank uses the ' + (isUnitType ? 'room type' : 'hotel') + ' name. NextPax rules: max 255 characters, '
                  + 'no emojis or * § ^, at most 3 capitals in a row and 50% capitals, a dot must be followed by a space.'}>
                <Form.Control value={form.name} maxLength={255}
                  placeholder={(property && property.default_name) || hotelName || 'Hotel name'}
                  onChange={e => set('name', e.target.value)} />
              </Field>
              <Field label="Property type" required hint="Only types NextPax allows for the selected classification">
                <CodeSelect category="property_types" value={form.type_code} onChange={v => set('type_code', v)} required
                  filter={c => !(c.data && c.data.classifications) || c.data.classifications.includes(form.classification)} />
              </Field>
              <Field label="Classification" required
                hint={id ? "Can't be changed after creation (NextPax doesn't allow it)"
                  : 'Multi-unit: a hotel or villa complex whose room types become unit types'}>
                <Form.Select value={form.classification} disabled={!!id}
                  onChange={e => setForm(prev => ({ ...prev, classification: e.target.value, type_code: '' }))}>
                  {(id ? [form.classification] : TOP_LEVEL_CLASSIFICATIONS).map(c => (
                    <option key={c} value={c}>{CLASSIFICATION_LABELS[c] || c}</option>
                  ))}
                </Form.Select>
              </Field>
              <Field label="Airbnb onboarding" required
                hint={isUnitType ? 'Inherited from the multi-unit when added' : 'Takeover maps an existing Airbnb listing; New lets NextPax build it'}>
                <Form.Select value={form.onboarding_mode} onChange={e => set('onboarding_mode', e.target.value)}>
                  <option value="takeover">Takeover (existing listing)</option>
                  <option value="new">New listing</option>
                </Form.Select>
              </Field>
              <Field label="Max occupancy" required>
                <Form.Control type="number" min={1} value={form.max_occupancy} required
                  onChange={e => set('max_occupancy', e.target.value)} />
              </Field>
              <Field label="Base currency" hint="Blank uses the property manager's main currency">
                <CodeSelect category="currencies" value={form.base_currency} onChange={v => set('base_currency', v)}
                  filter={c => PROPERTY_CURRENCIES.includes(c.code) || c.code === form.base_currency} />
              </Field>
              <Field label="License code">
                <Form.Control value={form.license_code || ''} onChange={e => set('license_code', e.target.value)} />
              </Field>
            </Row>
          </Card.Body>
        </Card>

        <Card className="margbot15">
          <Card.Header className="bld d-flex justify-content-between align-items-center">
            Descriptions
            <Button size="sm" variant="outline-primary"
              onClick={() => set('descriptions', [...form.descriptions, emptyDescription()])}>Add description</Button>
          </Card.Header>
          <Card.Body>
            {form.descriptions.map((d, index) => (
              <Row key={index} className={index > 0 ? 'border-top pt-2' : ''}>
                <Field label="Type" required md={3}>
                  <CodeSelect category="description_types" value={d.typeCode} required
                    onChange={v => setDescription(index, { typeCode: v })} />
                </Field>
                <Field label="Language" required md={2}>
                  <CodeSelect category="languages" value={d.language} required
                    onChange={v => setDescription(index, { language: v })} />
                </Field>
                <Field label="Text" required md={6}>
                  <Form.Control as="textarea" rows={3} value={d.text} required
                    onChange={e => setDescription(index, { text: e.target.value })} />
                </Field>
                <Col md={1} className="d-flex align-items-center">
                  {form.descriptions.length > 1 && (
                    <Button size="sm" variant="outline-danger"
                      onClick={() => set('descriptions', form.descriptions.filter((_, i) => i !== index))}>×</Button>
                  )}
                </Col>
              </Row>
            ))}
          </Card.Body>
        </Card>

        <Card className="margbot15">
          <Card.Header className="bld">Airbnb</Card.Header>
          <Card.Body>
            <Row>
              {listingIdRequired && (
                <Field label="Airbnb listing ID" required
                  hint="Numeric ID of the existing listing (airbnb.com/rooms/<ID>). Locked once sent to Airbnb.">
                  <Form.Control value={form.airbnb_listing_id || ''} inputMode="numeric"
                    onChange={e => set('airbnb_listing_id', e.target.value.trim())} />
                </Field>
              )}
              <Field label="Airbnb host ID" hint="Only when different from the property manager's. Locked once sent.">
                <Form.Control value={form.airbnb_host_id || ''} onChange={e => set('airbnb_host_id', e.target.value.trim())} />
              </Field>
              <Field label="Airbnb cancellation policy" hint="Optional override of the property manager's policy">
                <CodeSelect category="channel_policies" parentCode={AIRBNB_CHANNEL} value={form.airbnb_channel_policy_id}
                  onChange={v => set('airbnb_channel_policy_id', v)} />
              </Field>
            </Row>
          </Card.Body>
        </Card>

        <div className="margbot20">
          <Button type="submit" disabled={saving}>
            {saving ? <Spinner animation="border" size="sm" /> : (id ? 'Save changes' : 'Add property')}
          </Button>
          <Link to={id ? `/airbnb/properties/${id}` : `/airbnb/property-managers/${pmId}`} className="btn btn-link">Cancel</Link>
        </div>
      </Form>
    </div>
  );
};

export default PropertyForm;
